import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DistributionChannelSummary, DistributionProductRow, PublicationState } from '../../api';
import { listDistributionProducts } from '../../api';
import { useResource } from '../../hooks';
import { LevelBadge, PanelCard, QuietEmpty, SectionHeader } from '../project/ui';
import { ErrorNotice, LoadingRows } from '../ui';
import { channelLabel, formatWhen, publicationStateLabel, publicationStateLevel } from './labels';
import { Field, Input, Pager, Select, Table } from '../primitives';

const PAGE_SIZE = 50;

export interface ProductFilter {
  search: string;
  website: string;
  channel: string;
  state: string;
}

export const emptyProductFilter: ProductFilter = { search: '', website: '', channel: '', state: '' };

const stateOptions: { value: PublicationState | ''; label: string }[] = [
  { value: '', label: 'Any state' },
  { value: 'published', label: publicationStateLabel.published },
  { value: 'warning', label: publicationStateLabel.warning },
  { value: 'rejected', label: publicationStateLabel.rejected },
  { value: 'failed', label: publicationStateLabel.failed },
  { value: 'stale', label: publicationStateLabel.stale },
  { value: 'submitted', label: publicationStateLabel.submitted },
  { value: 'queued', label: publicationStateLabel.queued },
  { value: 'ineligible', label: publicationStateLabel.ineligible },
];

/**
 * The product table: one row per product per website, one column per
 * channel. It answers the question an operator actually asks — "is this
 * product out there, and if not, why not" — without opening anything.
 */
export function DistributionProductsPanel({
  projectId,
  channels,
  websites,
  filter,
  onFilterChange,
  onOpenProduct,
}: {
  projectId: string;
  channels: DistributionChannelSummary[];
  websites: { code: string; name: string }[];
  filter: ProductFilter;
  onFilterChange: (next: ProductFilter) => void;
  onOpenProduct: (projectionId: string) => void;
}) {
  const [offset, setOffset] = useState(0);
  const [draftSearch, setDraftSearch] = useState(filter.search);

  // The search box is debounced so typing does not issue a request per
  // keystroke against a hundred-thousand product catalogue.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (draftSearch !== filter.search) {
        setOffset(0);
        onFilterChange({ ...filter, search: draftSearch });
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [draftSearch, filter, onFilterChange]);

  useEffect(() => setDraftSearch(filter.search), [filter.search]);

  const products = useResource(
    () => listDistributionProducts(projectId, { ...filter, limit: PAGE_SIZE, offset }),
    [projectId, filter.search, filter.website, filter.channel, filter.state, offset],
    `${projectId}:${filter.website}:${filter.channel}:${filter.state}`, { key: `project:${projectId}:distribution-products:${filter.search}:${filter.website}:${filter.channel}:${filter.state}:${offset}` },
  );

  const channelTypes = useMemo(() => {
    const seen = new Set<string>();
    return channels
      .map((channel) => channel.channel_type)
      .filter((type) => (seen.has(type) ? false : (seen.add(type), true)));
  }, [channels]);

  const setFilter = useCallback(
    (patch: Partial<ProductFilter>) => {
      setOffset(0);
      onFilterChange({ ...filter, ...patch });
    },
    [filter, onFilterChange],
  );

  const rows = products.data?.rows ?? [];
  const total = products.data?.total ?? 0;

  return (
    <PanelCard labelledBy="distribution-products-title">
      <SectionHeader id="distribution-products-title" title="Products" />

      <div className="distribution-filters">
        <Field label="Search">
          <Input
            id="distribution-search"
            type="search"
            value={draftSearch}
            placeholder="SKU or product name"
            onChange={(event) => setDraftSearch(event.target.value)}
          />
        </Field>
        <Field label="Website">
          <Select
            id="distribution-website"
            value={filter.website}
            onChange={(event) => setFilter({ website: event.target.value })}
          >
            <option value="">Every website</option>
            {websites.map((website) => (
              <option key={website.code} value={website.code}>
                {website.name || website.code}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Channel">
          <Select
            id="distribution-channel"
            value={filter.channel}
            onChange={(event) => setFilter({ channel: event.target.value })}
          >
            <option value="">Every channel</option>
            {channelTypes.map((type) => (
              <option key={type} value={type}>
                {channelLabel[type]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="State">
          <Select
            id="distribution-state"
            value={filter.state}
            onChange={(event) => setFilter({ state: event.target.value })}
          >
            {stateOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {products.error ? <ErrorNotice error={products.error} /> : null}

      {products.loading && rows.length === 0 ? (
        <LoadingRows label="Loading products" rows={6} />
      ) : rows.length === 0 ? (
        <QuietEmpty testId="distribution-no-products">
          {filter.search || filter.state || filter.channel || filter.website ? 'No products match' : 'No products'}
        </QuietEmpty>
      ) : (
        <>
          <Table label="Products and where they are distributed" compact className="distribution-table">
            <thead>
              <tr>
                <th scope="col">Product</th>
                <th scope="col">SKU</th>
                <th scope="col">Website</th>
                {channelTypes.map((type) => (
                  <th key={type} scope="col">
                    {channelLabel[type]}
                  </th>
                ))}
                <th scope="col">Overall</th>
                <th scope="col">Last changed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <ProductRow key={row.projection_id} row={row} channelTypes={channelTypes} onOpen={onOpenProduct} />
              ))}
            </tbody>
          </Table>

          <Pager
            first={offset + 1}
            last={offset + rows.length}
            total={total}
            onPrevious={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            onNext={() => setOffset(offset + PAGE_SIZE)}
          />
        </>
      )}
    </PanelCard>
  );
}

function ProductRow({
  row,
  channelTypes,
  onOpen,
}: {
  row: DistributionProductRow;
  channelTypes: string[];
  onOpen: (projectionId: string) => void;
}) {
  return (
    <tr>
      <th scope="row">
        <button type="button" className="link-button" onClick={() => onOpen(row.projection_id)}>
          {row.title}
        </button>
        {!row.supported ? <span className="badge">{row.product_type}</span> : null}
      </th>
      <td>
        <code>{row.sku}</code>
      </td>
      <td>{row.website_code}</td>
      {channelTypes.map((type) => {
        const state = row.channels[type];
        return (
          <td key={type}>
            {state ? (
              <LevelBadge level={publicationStateLevel[state.state]}>{publicationStateLabel[state.state]}</LevelBadge>
            ) : (
              <span className="quiet-empty">Not in scope</span>
            )}
          </td>
        );
      })}
      <td>
        <LevelBadge level={publicationStateLevel[row.overall]}>{publicationStateLabel[row.overall]}</LevelBadge>
      </td>
      <td>{formatWhen(row.last_changed)}</td>
    </tr>
  );
}
